import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import {
  turkishRestaurants,
  burgerRestaurants,
  sushiRestaurants,
  mexicanRestaurants,
  pizzaRestaurants,
  fishRestaurants,
  steakRestaurants,
  koreanRestaurants,
  italianRestaurants,
  chineseRestaurants
} from '../../mocks/restaurants';

export default function Home() {
  const [showMore, setShowMore] = useState(false);

  useSEO({
    title: 'Antalya Restaurants Best Turkish Burger Sushi Mexican Antalya Eats',
    description: 'Discover Antalya\'s best restaurants featuring authentic Turkish cuisine, juicy burgers, fresh sushi, and spicy Mexican food. Your ultimate dining guide for tourists and locals. Explore top-rated restaurants in Antalya with reviews, menus, and contact information.',
    keywords: 'Antalya restaurants, Turkish food Antalya, best restaurants Antalya',
    canonical: '/',
    ogTitle: 'Antalya Eats - Best Restaurants in Antalya',
    ogDescription: 'Your ultimate guide to finding the best restaurants in Antalya',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Antalya Eats',
      'url': '{{SITE_URL}}/',
      'description': 'Your ultimate guide to finding the best restaurants in Antalya',
      'potentialAction': {
        '@type': 'SearchAction',
        'target': '{{SITE_URL}}/?search={search_term_string}',
        'query-input': 'required name=search_term_string'
      }
    }
  });

  const handleRandomRestaurant = () => {
    const categories = [
      { path: '/turkish-restaurants', restaurants: turkishRestaurants },
      { path: '/burger-restaurants', restaurants: burgerRestaurants },
      { path: '/sushi-restaurants', restaurants: sushiRestaurants },
      { path: '/mexican-restaurants', restaurants: mexicanRestaurants },
      { path: '/pizza-restaurants', restaurants: pizzaRestaurants },
      { path: '/fish-restaurants', restaurants: fishRestaurants },
      { path: '/steak-restaurants', restaurants: steakRestaurants },
      { path: '/korean-restaurants', restaurants: koreanRestaurants },
      { path: '/italian-restaurants', restaurants: italianRestaurants },
      { path: '/chinese-restaurants', restaurants: chineseRestaurants }
    ];
    
    // Flatten all restaurants into a single array with their category paths
    const allRestaurants = categories.flatMap(category => 
      category.restaurants.map(restaurant => ({
        ...restaurant,
        categoryPath: category.path
      }))
    );
    
    // Pick a random restaurant from all restaurants
    const randomRestaurant = allRestaurants[Math.floor(Math.random() * allRestaurants.length)];
    
    window.REACT_APP_NAVIGATE(`${randomRestaurant.categoryPath}?restaurant=${randomRestaurant.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/958b028c-edb9-4a2f-b10c-97937356ff1e.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <h1 className="text-lg font-bold tracking-tight">ANTALYA EATS</h1>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <main>
      <section className="pt-20 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            Discover Antalya's<br /><strong>Best Restaurants</strong>
          </h2>
          <p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">
            Your ultimate guide to finding <strong>authentic Turkish cuisine</strong>, <strong>juicy burgers</strong>, <strong>fresh sushi</strong>, <strong>delicious pizza</strong>, and <strong>fresh seafood in Antalya</strong>
          </p>
          <button
            onClick={handleRandomRestaurant}
            className="bg-rose-300 hover:bg-rose-400 text-gray-800 font-bold text-sm px-4 py-2 border-2 border-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            CAN'T DECIDE
          </button>
        </div>
      </section>

      {/* Category Selection */}
      <section className="pb-12 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-xl font-bold text-center mb-6">Choose Your Cuisine</h2>
          <div className="grid grid-cols-2 gap-3">
            {/* Turkish */}
            <Link 
              to="/turkish-restaurants"
              className="group relative bg-rose-200 hover:bg-rose-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 mb-3 flex items-center justify-center">
                  <i className="ri-restaurant-2-line text-4xl text-gray-800"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">TURKISH</h3>
                <p className="text-gray-700 text-xs">Authentic Turkish flavors</p>
              </div>
            </Link>

            {/* Burger */}
            <Link 
              to="/burger-restaurants"
              className="group relative bg-amber-200 hover:bg-amber-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 mb-3 flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/de26e89533d24c0934ba29e2e863c70a.png" 
                    alt="Burger" 
                    className="w-16 h-16 object-contain brightness-0"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">BURGER</h3>
                <p className="text-gray-700 text-xs">Juicy burgers</p>
              </div>
            </Link>

            {/* Sushi */}
            <Link 
              to="/sushi-restaurants"
              className="group relative bg-pink-200 hover:bg-pink-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 mb-3 flex items-center justify-center">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/ae2f4b8c-f4e6-4c33-b7e6-e554c1c48200.png" 
                    alt="Sushi" 
                    className="w-16 h-16 object-contain brightness-0"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">SUSHI</h3>
                <p className="text-gray-700 text-xs">Fresh sushi</p>
              </div>
            </Link>

            {/* Mexican */}
            <Link 
              to="/mexican-restaurants"
              className="group relative bg-emerald-200 hover:bg-emerald-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 mb-3 flex items-center justify-center">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/4954026c-56fd-423b-9e9f-3b8ef5cf602b.png" 
                    alt="Mexican" 
                    className="w-12 h-12 object-contain brightness-0"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">MEXICAN</h3>
                <p className="text-gray-700 text-xs">Spicy tacos</p>
              </div>
            </Link>

            {/* Pizza */}
            <Link 
              to="/pizza-restaurants"
              className="group relative bg-orange-200 hover:bg-orange-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 mb-3 flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/125bef39ecd7be890385735c930e226d.png" 
                    alt="Pizza" 
                    className="w-20 h-20 object-contain brightness-0"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">PIZZA</h3>
                <p className="text-gray-700 text-xs">Wood-fired pizza</p>
              </div>
            </Link>

            {/* Fish */}
            <Link 
              to="/fish-restaurants"
              className="group relative bg-sky-200 hover:bg-sky-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 mb-3 flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/e2d2f10671c6669a30f73e642e3927f9.png" 
                    alt="Fish" 
                    className="w-20 h-20 object-contain brightness-0"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">FISH</h3>
                <p className="text-gray-700 text-xs">Fresh seafood</p>
              </div>
            </Link>

            {/* Additional Categories - Show when expanded */}
            {showMore && (
              <>
                {/* Steak */}
                <Link 
                  to="/steak-restaurants"
                  className="group relative bg-rose-200 hover:bg-rose-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 mb-3 flex items-center justify-center">
                      <i className="ri-restaurant-line text-4xl text-gray-800"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">STEAK</h3>
                    <p className="text-gray-700 text-xs">Premium steaks</p>
                  </div>
                </Link>

                {/* Korean */}
                <Link 
                  to="/korean-restaurants"
                  className="group relative bg-emerald-200 hover:bg-emerald-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 mb-3 flex items-center justify-center">
                      <i className="ri-bowl-fill text-4xl text-gray-800"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">KOREAN</h3>
                    <p className="text-gray-700 text-xs">Korean BBQ & more</p>
                  </div>
                </Link>

                {/* Persian */}
                <Link 
                  to="/persian-restaurants"
                  className="group relative bg-orange-200 hover:bg-orange-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 mb-3 flex items-center justify-center">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/6e00a8d0-dd6a-4b5c-9736-fee7e129412a.png" 
                        alt="Persian Kebab" 
                        className="w-16 h-16 object-contain brightness-0"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">PERSIAN</h3>
                    <p className="text-gray-700 text-xs">Persian delights</p>
                  </div>
                </Link>

                {/* Italian */}
                <Link 
                  to="/italian-restaurants"
                  className="group relative bg-sky-200 hover:bg-sky-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 mb-3 flex items-center justify-center">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/19fa2e07-d981-4d45-af51-5cc0265b7e39.png" 
                        alt="Italian" 
                        className="w-16 h-16 object-contain brightness-0"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">ITALIAN</h3>
                    <p className="text-gray-700 text-xs">Italian classics</p>
                  </div>
                </Link>

                {/* Chinese */}
                <Link 
                  to="/chinese-restaurants"
                  className="group relative bg-amber-200 hover:bg-amber-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 mb-3 flex items-center justify-center">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/218960c6-7669-47d0-b2cf-b28d21b21682.png" 
                        alt="Chinese" 
                        className="w-16 h-16 object-contain brightness-0"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">CHINESE</h3>
                    <p className="text-gray-700 text-xs">Authentic Chinese</p>
                  </div>
                </Link>

                {/* Drinks */}
                <Link 
                  to="/drinks-restaurants"
                  className="group relative bg-pink-200 hover:bg-pink-300 transition-colors border-4 border-gray-800 p-4 cursor-pointer"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 mb-3 flex items-center justify-center">
                      <i className="ri-goblet-line text-4xl text-gray-800"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">DRINKS</h3>
                    <p className="text-gray-700 text-xs">Coffee & Cocktails</p>
                  </div>
                </Link>
              </>
            )}
          </div>

          {/* Slider Button */}
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setShowMore(!showMore)}
              className="w-12 h-12 rounded-full bg-gray-800 hover:bg-gray-700 border-4 border-gray-800 flex items-center justify-center transition-all cursor-pointer shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
              aria-label={showMore ? "Show less categories" : "Show more categories"}
            >
              <i className={`ri-arrow-${showMore ? 'up' : 'down'}-s-line text-2xl text-white transition-transform`}></i>
            </button>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Website Builder</a>
        </div>
      </footer>
    </div>
  );
}
